// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import * as Sentry from '@sentry/browser';

interface ArtifactErrorBoundaryProps {
  children: React.ReactNode;
  // What failed, for the inline fallback text (e.g. "chart", "map chart").
  label?: string;
}

interface ArtifactErrorBoundaryState {
  hasError: boolean;
}

/**
 * A GRANULAR error boundary for a single rendered artifact — a Khora chart, a map-popup chart, a
 * restored session item. It catches a render error LOCALLY and shows a small inline fallback, so one
 * malformed / stale / half-parsed artifact can't bubble up to the app-level ErrorScreen and blank the
 * whole page. Especially important for session-restored artifacts (stale specs, expired URLs, schema
 * drift). Reports the error to Sentry (a no-op when Sentry isn't configured, e.g. in dev).
 */
class ArtifactErrorBoundary extends React.Component<ArtifactErrorBoundaryProps, ArtifactErrorBoundaryState> {
  constructor(props: ArtifactErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ArtifactErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error);
  }

  render() {
    const { hasError } = this.state;
    const { children, label } = this.props;
    if (hasError) {
      return (
        <div style={{ fontSize: '0.72rem', color: '#8a8a8a', fontStyle: 'italic', padding: '6px 8px' }}>
          {`Couldn’t render this ${label || 'item'}.`}
        </div>
      );
    }
    return children;
  }
}

export default ArtifactErrorBoundary;
